import { getToken } from '@/utils/auth';
import { BASE_URL } from '@/utils/constants';
import request from '@/utils/request';

const getLocalStorageLanguage = () => {
  return typeof window !== 'undefined'
    ? window.localStorage.getItem('language')
    : '';
};

const normalizeShortAiLanguage = (language) => {
  const localLanguage = getLocalStorageLanguage();
  if (!localLanguage) return 'zh';

  const normalizedLang = String(language || localLanguage).trim().toLowerCase();

  return /^en/.test(normalizedLang) ? 'en' : 'zh';
};

const getStreamingRequestLang = (lang) => {
  return normalizeShortAiLanguage(lang);
};

// 获取聊天记录
export const getChatHistory = (chatId) => {
  return request({
    url: `/api/standard/odata/UserCopilotChatHistories?$filter=user_copilot_chat_id eq '${chatId}'&$orderby=chat_time asc`,
    method: 'get'
  });
};

// 获取用户的会话列表 (支持分页)
export const getChatSessions = (userId, roleId, pageNum = 1, pageSize = 10) => {
  const skip = (pageNum - 1) * pageSize;
  let filter = `user_id eq '${userId}'`;
  if (roleId) {
    filter += ` and role_id eq '${roleId}'`;
  }
  return request({
    url: `/api/standard/odata/UserCopilotChats?$filter=${filter}&$orderby=chat_time desc&$skip=${skip}&$top=${pageSize}&$count=true`,
    method: 'get'
  });
};

// 保存会话Session
export const saveChatSession = (data) => {
  const payload = {
    session_id: data.session_id,
    user_id: data.user_id,
    role_id: data.role_id,
    chat_title: data.chat_title,
    chat_time: data.chat_time
  };
  return request({
    url: '/api/standard/odata/UserCopilotChats',
    method: 'post',
    data: payload
  });
};

// 删除会话Session
export const deleteChatSession = (id) => {
  return request({
    url: `/api/standard/odata/UserCopilotChats('${id}')`,
    method: 'delete'
  });
};

// 获取AI要点速览
export const getPhysicianAiSummary = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiSummaries?$filter=physician_id eq '${physicianId}'&$orderby=summary_time desc&$top=1`,
    method: 'get'
  });
};

// 保存AI要点速览
export const savePhysicianAiSummary = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiSummaries',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      summary: data.summary,
      summary_time: data.summary_time
    }
  });
};

// 获取拜访反馈总结
export const getPhysicianAiFeedbackSummary = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiFeedbackSummaries?$filter=physician_id eq '${physicianId}'&$orderby=summary_time desc&$top=1`,
    method: 'get'
  });
};

// 保存拜访反馈总结
export const savePhysicianAiFeedbackSummary = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiFeedbackSummaries',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      summary: data.summary,
      summary_time: data.summary_time
    }
  });
};

// 获取拜访策略
export const getPhysicianAiCallStrategy = (physicianId) => {
  return request({
    url: `/api/standard/odata/PhysicianAiCallStrategies?$filter=physician_id eq '${physicianId}'&$orderby=strategy_time desc&$top=1`,
    method: 'get'
  });
};

// 保存拜访策略
export const savePhysicianAiCallStrategy = (data) => {
  return request({
    url: '/api/standard/odata/PhysicianAiCallStrategies',
    method: 'post',
    data: {
      physician_id: data.physician_id,
      strategy: data.strategy,
      strategy_time: data.strategy_time
    }
  });
};

// 保存聊天记录
export const saveChatHistory = (data) => {
  const payload = {
    user_copilot_chat_id: data.user_copilot_chat_id,
    session_id: data.session_id,
    user_id: data.user_id,
    role_id: data.role_id,
    chat_role: data.chat_role,
    content: data.content,
    chat_time: data.chat_time
  };
  if (data.think_seconds !== undefined && data.think_seconds !== null) {
    payload.think_seconds = data.think_seconds;
  }
  if (data.total_seconds !== undefined && data.total_seconds !== null) {
    payload.total_seconds = data.total_seconds;
  }
  if (data.thoughts !== undefined && data.thoughts !== null) {
    payload.thoughts = data.thoughts;
  }
  return request({
    url: '/api/standard/odata/UserCopilotChatHistories',
    method: 'post',
    data: payload
  });
};

export const streamSuperRepCopilot = ({
  content,
  sessionId,
  agentName = 'SRCopilot',
  enableThinking = false,
  hasThoughts = false,
  onChunk,
  onComplete,
  onError
}) => {
  const payload = {
    content,
    session_id: sessionId || null,
    enable_thinking: Boolean(enableThinking),
    has_thoughts: Boolean(hasThoughts)
  };
  return streamBailianRequest(agentName || 'SRCopilot', payload, { onChunk, onComplete, onError });
};

const normalizeAiLanguage = (language) => {
  const localLanguage = getLocalStorageLanguage();
  if (!localLanguage) return 'zh-CN';

  if (!language) return '';
  if (/^en/i.test(language)) return 'en-US';
  if (/^zh/i.test(language)) return 'zh-CN';
  return language;
};

const shouldEmbedLanguageInContent = (agentName) => {
  return ['SRHCPSummary', 'SRHCPFeedback'].includes(agentName);
};

// 通用流式请求助手
const streamBailianRequest = async (agentName, payload, callbacks) => {
  const { onChunk, onComplete, onError } = callbacks;
  const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
  const url = `${baseUrl}api/ai/Bailian/Agent/${agentName}`;
  const requestPayload = { ...payload };
  const normalizedLanguage = normalizeAiLanguage(requestPayload.language || requestPayload.locale);
  const embedLanguageInContent = shouldEmbedLanguageInContent(agentName) && typeof requestPayload.content === 'string';

  if (normalizedLanguage) {
    if (embedLanguageInContent) {
      const hasLanguageInContent = /(^|&)language=/.test(requestPayload.content);
      const contentLanguage = normalizeShortAiLanguage(requestPayload.language || requestPayload.locale);
      requestPayload.content = hasLanguageInContent
        ? requestPayload.content
        : `${requestPayload.content}&language=${encodeURIComponent(contentLanguage)}`;
    } else {
      requestPayload.language = normalizedLanguage;
    }
  }

  if (embedLanguageInContent) {
    delete requestPayload.language;
  }
  delete requestPayload.locale;

  try {
    const token = getToken()?.token;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({
        ...requestPayload,
        lang: getStreamingRequestLang(requestPayload && requestPayload.lang),
        incremental_output: true,
        sse: true
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let count = 0;
    while (count < Number.MAX_VALUE) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      if (onChunk) onChunk(chunk);

      count++;
    }

    if (onComplete) onComplete();
  } catch (error) {
    if (onError) onError(error);
  }
};

// 总结实时录入的拜访反馈 (Stream)
export const streamFeedbackSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRFeedbackSummary', data, { onChunk, onComplete, onError });
};

// SRHCPSummary (Stream)
export const streamHCPSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRHCPSummary', data, { onChunk, onComplete, onError });
};

// SRHCPFeedback (Stream)
export const streamHCPFeedback = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRHCPFeedback', data, { onChunk, onComplete, onError });
};

// SRCallSummary (Stream)
export const streamCallSummary = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRCallSummary', data, { onChunk, onComplete, onError });
};

// 获取下一步最佳行动建议 (Stream)
export const streamNextBestActionSuggestion = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRNextBestActionSuggestion', data, { onChunk, onComplete, onError });
};

// 获取下一步最佳行动建议
export const getNextBestActionSuggestion = (data = {}) => {
  const payload = { ...data };

  return request({
    url: '/api/ai/Bailian/Agent/SRNextBestActionSuggestion',
    method: 'post',
    data: {
      content: payload.content || '',
      lang: getStreamingRequestLang(payload && payload.lang),
      incremental_output: false,
      sse: false
    }
  });
};

// 获取用户最新客户动态总结
export const getUserRecentHcpUpdateSummary = (userId, roleId) => {
  return request({
    url: `/api/standard/odata/UserRecentHcpUpdateSummaries?$filter=user_id eq '${userId}' and role_id eq '${roleId}'&$orderby=create_time desc&$top=1`,
    method: 'get'
  });
};

// 保存用户最新客户动态总结
export const saveUserRecentHcpUpdateSummary = (data) => {
  return request({
    url: '/api/standard/odata/UserRecentHcpUpdateSummaries',
    method: 'post',
    data: {
      user_id: data.user_id,
      role_id: data.role_id,
      summary: data.summary,
      create_time: data.create_time
    }
  });
};

// SRMyHCPUpdates (Stream)
export const streamMyHCPUpdates = ({ data, onChunk, onComplete, onError }) => {
  return streamBailianRequest('SRMyHCPUpdates', data, { onChunk, onComplete, onError });
};
